var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector, Store } from '@ngxs/store';
import { BookmarksStateModel } from './bookmarks.state.model';
import { LoadBookmarks, LoadBookmarksSuccess, LoadOrganizationBookmarks, DeleteBookmark, DeleteBookmarkSuccess, SaveBookmark, SaveBookmarkSuccess, SelectBookmark, SelectBookmarkSuccess, SaveBookmarkFail, SaveBookmarkThumbnails, ResetBookmarks, SortBookmarks, CheckBookmark, SetCheckedBookmarks, ExportBookmarks, ExportBookmarksSuccess, ExportBookmarksError, LoadCollectionBookmarks, DeleteBookmarkFail, DeleteFavorite, DeleteFavoriteSuccess, DeleteFavoriteFail, CopyBookmark, CopyBookmarkFailure, CopyBookmarkSuccess, SetBookmarkText, SetSelectedCategory, ClearBookmarks, UpdateBookmarkCollection, UpdateBookmarkCollectionSuccess, UpdateBookmarkCollectionError, RemoveBookmarkCollectionSuccess, AddBookmarkCollectionSuccess, RemoveBookmarkCollectionError, AddBookmarkCollectionError } from './bookmarks.actions';
import { finalize, flatMap, filter, tap, map, toArray } from 'rxjs/operators';
import { BookmarksService } from '../bookmarks.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { concat, forkJoin, of } from 'rxjs';
import { RegisterChapterThumbnails } from 'src/app/story/state/chapters/story.chapters.actions';
import { bookmarkSortRecent } from '../data/bookmark-sort';
import { PublishService } from 'src/app/publish/publish.service';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { StoryService } from 'src/app/story/story.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { ExploreService } from 'src/app/explore/explore.service';
import { get } from 'lodash';
import { Thumbnail } from 'src/app/shared/thumbnail-utils';
import { sortModels } from '../models/bookmark-sort.model';
const BookmarksStateDefaults = {
    loading: true,
    exporting: false,
    copying: false,
    saving: false,
    bookmarks: [],
    selectedBookmark: null,
    sort: bookmarkSortRecent,
    checkedBookmarks: [],
    selectedCategory: null
};
let BookmarksState = class BookmarksState {
    constructor(store, storyService, bookmarksService, exploreService, publishService, organizationsService) {
        this.store = store;
        this.storyService = storyService;
        this.bookmarksService = bookmarksService;
        this.exploreService = exploreService;
        this.publishService = publishService;
        this.organizationsService = organizationsService;
    }
    static getBookmarks(state) {
        const { bookmarks, sort } = state;
        const { ascending, fields } = sort;
        return sortModels(bookmarks, fields, ascending);
    }
    static getCheckedBookmarks(state) {
        return state.checkedBookmarks;
    }
    static getSelectedBookmark(state) {
        return state.selectedBookmark;
    }
    static getLoading(state) {
        return state.loading;
    }
    static getExporting(state) {
        return state.exporting;
    }
    static getSaving(state) {
        return state.saving;
    }
    static getCopying(state) {
        return state.copying;
    }
    static getSort(state) {
        return state.sort;
    }
    static getSelectedCategory(state) {
        return state.selectedCategory;
    }
    loadBookmarks(context, action) {
        context.patchState({
            loading: true,
            bookmarks: [],
            checkedBookmarks: []
        });
        // load private content (private bookmarks, custom modules, and favorites)
        forkJoin(this.bookmarksService.getPrivateBookmarks(action.query), this.bookmarksService.getPrivateConditions(), this.bookmarksService.getFavorites())
            .pipe(finalize(() => { context.patchState({ loading: false }); }))
            .subscribe(([bookmarks, conditions, favorites]) => {
            context.dispatch(new LoadBookmarksSuccess([
                ...bookmarks,
                ...conditions,
                ...favorites
            ]));
        });
    }
    loadOrganizationBookmarks(context, { organizationId }) {
        context.patchState({
            loading: true,
            bookmarks: [],
            checkedBookmarks: []
        });
        // load group content (private bookmarks and custom modules)
        this.bookmarksService.getOrganizationContent(organizationId)
            .pipe(finalize(() => { context.patchState({ loading: false }); }))
            .subscribe((bookmarks) => {
            context.dispatch(new LoadBookmarksSuccess(bookmarks));
        });
    }
    loadCollectionBookmarks(context, action) {
        context.patchState({
            loading: true,
            bookmarks: [],
            checkedBookmarks: []
        });
        this.organizationsService.getCollectionContent(action.organizationId, action.collectionId)
            .pipe(finalize(() => { context.patchState({ loading: false }); }))
            .subscribe((result) => {
            context.dispatch(new LoadBookmarksSuccess(result));
        });
    }
    loadSuccess({ patchState }, action) {
        patchState({
            bookmarks: action.result
        });
    }
    loadBookmark(context, action) {
        const { id, type } = action;
        const bookmark$ = type === 'bookmark'
            ? this.bookmarksService.getBookmarkModel(id)
            : this.exploreService.getConditionModel(id);
        bookmark$
            .subscribe((bookmark) => {
            context.dispatch(new SelectBookmarkSuccess(bookmark));
        });
    }
    selectBookmarkSuccess(context, action) {
        context.patchState({
            selectedBookmark: action.bookmark
        });
    }
    // Replace existing or Save new bookmark
    saveBookmark(context, action) {
        const bookmarkEntry = action.bookmarkEntry;
        // begin save
        context.patchState({ saving: true });
        // requires story
        let serialized;
        if (action.includeIndex) {
            const labels = this.store.selectSnapshot(StoryLabelsState.getLabels);
            const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
            this.storyService.storeLabelsInChapters(labels, chapters);
            serialized = this.bookmarksService.createBookmarkStory(action.inplace); // Just a serialized module
            bookmarkEntry.index = serialized.story;
        }
        const method = bookmarkEntry.id ? 'update' : 'save';
        this.bookmarksService.saveBookmark(bookmarkEntry)
            .pipe(finalize(() => {
            context.patchState({ saving: false });
        }))
            .subscribe((bookmark) => {
            // save thumbnails
            if (action.includeIndex) {
                const thumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
                context.dispatch(new SaveBookmarkThumbnails(method, bookmark.id, bookmarkEntry, thumbnails, serialized.chapterIdMap));
            }
            // add/remove collections
            if (bookmarkEntry.addCollection || bookmarkEntry.removeCollection) {
                context.dispatch(new UpdateBookmarkCollection(bookmark, bookmarkEntry.addCollection, bookmarkEntry.removeCollection));
            }
            const story = serialized ? serialized.story : null;
            context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
        }, (err) => {
            context.dispatch(new SaveBookmarkFail(bookmarkEntry));
        });
    }
    saveBookmarkSuccess(context, action) {
        const savedBookmark = action.bookmark;
        const bookmarks = context.getState().bookmarks;
        // patch bookmark entry
        const savedBookmarkIndex = bookmarks.findIndex(bookmark => bookmark.id === savedBookmark.id);
        if (savedBookmarkIndex !== -1) {
            bookmarks[savedBookmarkIndex] = savedBookmark;
            context.patchState({
                bookmarks: [...bookmarks]
            });
        }
        // update selected bookmar if replacing (save)
        if (action.method === 'save') {
            context.patchState({
                selectedBookmark: action.bookmark
            });
        }
    }
    deleteBookmark(context, action) {
        const bookmarkId = action.bookmarkId;
        this.bookmarksService.deleteBookmark(bookmarkId)
            .subscribe(() => {
            context.dispatch(new DeleteBookmarkSuccess(bookmarkId));
        }, (message) => {
            context.dispatch(new DeleteBookmarkFail(bookmarkId, message));
        });
    }
    deleteBookmarkSuccess(context, action) {
        const bookmarks = context.getState().bookmarks
            .filter(bookmark => bookmark.id !== action.bookmarkId);
        context.patchState({
            bookmarks: [...bookmarks]
        });
    }
    deleteFavorite(context, action) {
        const bookmark = action.bookmark;
        const bookmarkId = bookmark.id;
        // category normalization: 'condition' | 'anatomy' | 'bookmark'
        const contentType = get(bookmark, 'category', 'bookmark');
        // id normalization
        const contentId = contentType === 'bookmark' ? bookmarkId : this.exploreService.toDatabaseContentId(bookmarkId);
        this.bookmarksService.deleteFavorite(contentId, contentType)
            .subscribe(() => {
            context.dispatch(new DeleteFavoriteSuccess(bookmarkId));
        }, (err) => {
            context.dispatch(new DeleteFavoriteFail(bookmarkId));
        });
    }
    deleteFavoriteSuccess(context, action) {
        const bookmarks = context.getState().bookmarks
            .filter(bookmark => bookmark.id !== action.bookmarkId);
        context.patchState({
            bookmarks: [...bookmarks]
        });
    }
    sortBookmarks(context, action) {
        const bookmarks = context.getState().bookmarks;
        context.patchState({
            sort: action.sort,
            bookmarks: [...bookmarks]
        });
    }
    clearBookmarks(context, action) {
        context.patchState({
            bookmarks: [],
            checkedBookmarks: []
        });
    }
    resetBookmarks(context, action) {
        context.setState(BookmarksStateDefaults);
    }
    checkBookmark(context, action) {
        let checkedBookmarks = context.getState().checkedBookmarks;
        const exists = checkedBookmarks.some(bookmark => bookmark === action.id);
        if (action.checked && !exists) {
            checkedBookmarks = [...checkedBookmarks, action.id];
        }
        else if (!action.checked) {
            checkedBookmarks = checkedBookmarks.filter(bookmark => bookmark !== action.id);
        }
        context.patchState({
            checkedBookmarks: checkedBookmarks
        });
    }
    setCheckedBookmarks(context, action) {
        context.patchState({
            checkedBookmarks: action.ids
        });
    }
    saveBookmarkThumbnails(context, action) {
        // chapter id mapping (new bookmark chapters have new ids)
        const chapterIdMap = action.chapterIdMap;
        // thumbnail urls on server are identified by story-id and chapter-id.
        const thumbnails = action.thumbnails;
        // replace/create thumbnails
        const requests$ = this.bookmarksService.saveBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);
        // conditionally delete thumbnails
        if (action.method === 'update') {
            // Delete extra thumbnails. We are saving over an existing bookmark
            const deleteRequests$ = this.bookmarksService.deleteBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);
            for (const deleteRequest$ of deleteRequests$) {
                requests$.push(deleteRequest$);
            }
        }
        forkJoin(requests$).subscribe((results) => {
            const thumbnailLookup = action.bookmarkEntry.index.chapters.map(chapter => {
                const originalStoryId = action.bookmarkId;
                const newChapterId = chapter.id;
                const originalChapterId = newChapterId;
                return { originalStoryId, newChapterId, originalChapterId };
            });
            const _thumbnails = Thumbnail.generateThumbnailUrls(thumbnailLookup);
            // Chapter thumbnails need to be flushed so thumbnails are properly fetched.
            context.dispatch(new RegisterChapterThumbnails(new Map())).subscribe(() => {
                context.dispatch(new RegisterChapterThumbnails(_thumbnails));
            });
        });
    }
    exportBookmarks(context, action) {
        const { bookmarks, widgetURL, widgetOptions, offlineEnabled } = action;
        const exportWidgetOptions = widgetOptions || new WidgetOptions();
        context.patchState({ exporting: true });
        this.publishService.exportModels(bookmarks, widgetURL, exportWidgetOptions, null, action.userLanguage, action.userApp, offlineEnabled)
            .pipe(finalize(() => {
            context.patchState({ exporting: false });
        }))
            .subscribe((success) => {
            if (success) {
                context.dispatch(new ExportBookmarksSuccess(bookmarks));
            }
            else {
                context.dispatch(new ExportBookmarksError(bookmarks));
            }
        });
    }
    updateBookmarkCollection(context, action) {
        const { bookmark, addCollection, removeCollection } = action;
        const collectionContent = {
            content_type: bookmark.type,
            content_id: bookmark.id
        };
        const operations$ = [];
        // #TODO[AC]: service updates to tidy app side bulk updates
        // important: removal need before adding, if applicable
        if (removeCollection) {
            operations$.push(
            // get bookmark's collection content id
            this.organizationsService.getCollectionContentId(removeCollection, collectionContent)
                .pipe(
            // validate id
            filter((collectionContentId) => !!collectionContentId), 
            // remove bookmark via collection content id
            flatMap((collectionContentId) => this.organizationsService.removeFromCollection(removeCollection, [collectionContentId])), tap(() => context.dispatch(new RemoveBookmarkCollectionSuccess(bookmark, removeCollection)), () => context.dispatch(new RemoveBookmarkCollectionError(bookmark, removeCollection)))));
        }
        // optionally: add to collection
        if (addCollection) {
            operations$.push(
            // add content to specified collection
            this.organizationsService.addToCollection(addCollection, [collectionContent])
                .pipe(tap(() => context.dispatch(new AddBookmarkCollectionSuccess(bookmark, removeCollection)), () => context.dispatch(new AddBookmarkCollectionError(bookmark, removeCollection)))));
        }
        // execute operations
        concat(...operations$)
            .subscribe(() => context.dispatch(new UpdateBookmarkCollectionSuccess(bookmark, addCollection, removeCollection)), () => context.dispatch(new UpdateBookmarkCollectionError(bookmark, addCollection, removeCollection)));
    }
    removeBookmarkCollectionSuccess(context, action) {
        const { bookmarks, selectedCategory } = context.getState();
        // if current state bookmarks are from collection data: update list
        if (selectedCategory === 'collection') {
            const bookmarkId = action.bookmark.id;
            const patchBookmarks = bookmarks
                .filter(bookmark => bookmark.id !== bookmarkId);
            context.patchState({
                bookmarks: [...patchBookmarks]
            });
        }
    }
    copyBookmark(context, action) {
        const { groupAccess, collection } = action;
        context.patchState({ copying: true });
        this.bookmarksService.copyBookmark(action.id, action.type) // copy bookmark
            .pipe(flatMap((id) => {
            const operations$ = [of(id)]; // required: bookmark id
            if (groupAccess) { // optional: give group access
                operations$.push(this.bookmarksService.saveBookmark({ isOwner: true, id: id, add_group_access: [groupAccess] }));
                if (collection) { // optional: add to collection
                    operations$.push(this.organizationsService.addToCollection(collection, [{ content_type: 'bookmark', content_id: id }]));
                }
            }
            return concat(...operations$)
                .pipe(toArray(), map(() => id));
        }), finalize(() => {
            context.patchState({ copying: false });
        }))
            .subscribe((id) => {
            context.dispatch(new CopyBookmarkSuccess(id, action.id, groupAccess, collection));
        }, (error) => {
            context.dispatch(new CopyBookmarkFailure(action.id, error));
        });
    }
    setBookmarkText(context, action) {
        const selectedBookmark = context.getState().selectedBookmark;
        context.patchState({
            selectedBookmark: Object.assign({}, selectedBookmark, { title: action.title })
        });
    }
    setSelectedCategory(context, { category }) {
        context.patchState({
            selectedCategory: category ? category : null
        });
    }
};
__decorate([
    Action(LoadBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LoadBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "loadBookmarks", null);
__decorate([
    Action(LoadOrganizationBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LoadOrganizationBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "loadOrganizationBookmarks", null);
__decorate([
    Action(LoadCollectionBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LoadCollectionBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "loadCollectionBookmarks", null);
__decorate([
    Action(LoadBookmarksSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LoadBookmarksSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "loadSuccess", null);
__decorate([
    Action(SelectBookmark),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SelectBookmark]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "loadBookmark", null);
__decorate([
    Action(SelectBookmarkSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SelectBookmarkSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "selectBookmarkSuccess", null);
__decorate([
    Action(SaveBookmark),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SaveBookmark]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "saveBookmark", null);
__decorate([
    Action(SaveBookmarkSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SaveBookmarkSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "saveBookmarkSuccess", null);
__decorate([
    Action(DeleteBookmark),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, DeleteBookmark]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "deleteBookmark", null);
__decorate([
    Action(DeleteBookmarkSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, DeleteBookmarkSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "deleteBookmarkSuccess", null);
__decorate([
    Action(DeleteFavorite),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, DeleteFavorite]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "deleteFavorite", null);
__decorate([
    Action(DeleteFavoriteSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, DeleteFavoriteSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "deleteFavoriteSuccess", null);
__decorate([
    Action(SortBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SortBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "sortBookmarks", null);
__decorate([
    Action(ClearBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ClearBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "clearBookmarks", null);
__decorate([
    Action(ResetBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "resetBookmarks", null);
__decorate([
    Action(CheckBookmark),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CheckBookmark]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "checkBookmark", null);
__decorate([
    Action(SetCheckedBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetCheckedBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "setCheckedBookmarks", null);
__decorate([
    Action(SaveBookmarkThumbnails),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SaveBookmarkThumbnails]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "saveBookmarkThumbnails", null);
__decorate([
    Action(ExportBookmarks),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ExportBookmarks]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "exportBookmarks", null);
__decorate([
    Action(UpdateBookmarkCollection),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, UpdateBookmarkCollection]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "updateBookmarkCollection", null);
__decorate([
    Action(RemoveBookmarkCollectionSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveBookmarkCollectionSuccess]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "removeBookmarkCollectionSuccess", null);
__decorate([
    Action(CopyBookmark),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CopyBookmark]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "copyBookmark", null);
__decorate([
    Action(SetBookmarkText),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetBookmarkText]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "setBookmarkText", null);
__decorate([
    Action(SetSelectedCategory),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetSelectedCategory]),
    __metadata("design:returntype", void 0)
], BookmarksState.prototype, "setSelectedCategory", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getBookmarks", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getCheckedBookmarks", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getSelectedBookmark", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getLoading", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getExporting", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getSaving", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getCopying", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getSort", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BookmarksStateModel]),
    __metadata("design:returntype", void 0)
], BookmarksState, "getSelectedCategory", null);
BookmarksState = __decorate([
    State({
        name: 'bookmarks',
        defaults: BookmarksStateDefaults
    }),
    __metadata("design:paramtypes", [Store,
        StoryService,
        BookmarksService,
        ExploreService,
        PublishService,
        OrganizationsService])
], BookmarksState);
export { BookmarksState };
